import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {GetPipelineConfigSpecType, PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {JSONArray, JSONObject, JSONValue} from 'util/Types';

import jsonSpecToInitialValue from '../initial-values/jsonSpecToInitialValue';
import {useStepConfigEditorContext} from '../StepConfigEditorContext';
import {
  getFieldSettingsIfPresent,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  TaggedUnionUISchema,
} from '../StepConfigEditorTypes';
import AddArrayElementIcon from './AddArrayElementIcon';
import getSortedSubfields from './getSortedSubfields';
import StepConfigEditorRouter from './StepConfigEditorRouter';
import StepConfigFieldsTemplate from './StepConfigFieldsTemplate';

interface TaggedUnionTagKeyField {
  kind: 'string';
  value: string;
  required: boolean;
}

function isTaggedUnionChoice(spec: PipelineConfigJsonSpec): spec is TaggedUnionTagKeyField {
  return spec.kind === 'string' && spec.value != null;
}

/** @package */
export function TagChoiceDropdown({
  selectedTag,
  onChangeChoice,
  uiSchema,
  configShape,
  disabled,
  buttonSize,
}: {
  selectedTag: string | null;
  onChangeChoice: (choiceShape: GetPipelineConfigSpecType<'shape'>) => void;
  uiSchema?: TaggedUnionUISchema;
  configShape: GetPipelineConfigSpecType<'taggedUnion'>;
  disabled?: boolean;
  buttonSize?: ButtonSize;
}): JSX.Element {
  const tagKey = configShape.tagKey;

  function getTagOptionDisplayName(tagOption: string): string {
    return uiSchema?.tagKeyDisplayNames?.[tagOption] ?? tagOption;
  }

  const menuOptions: Array<OptionData> = configShape.choices.map((choiceShape) => {
    const tagKeySpec = choiceShape.fields[tagKey];
    assert(tagKeySpec != null && isTaggedUnionChoice(tagKeySpec), 'Invalid tagged union choice found');
    return {
      name: getTagOptionDisplayName(tagKeySpec.value),
      value: tagKeySpec.value,
    };
  });

  const handleSelect = (value: string): void => {
    for (const choice of configShape.choices) {
      const tagKeySpec = choice.fields[tagKey];
      assert(tagKeySpec != null && isTaggedUnionChoice(tagKeySpec), 'Invalid tagged union choice found');
      if (tagKeySpec.value === value) {
        onChangeChoice(choice);
        return;
      }
    }
    assert(false, 'No matching choice found');
  };

  return (
    <SelectDropdown
      className={css(styles.grow)}
      dataTestAttrs={{component: 'StepConfigEditor', element: 'dropdown', key: tagKey}}
      displayName={
        selectedTag != null
          ? getTagOptionDisplayName(selectedTag)
          : (uiSchema?.placeholder ?? 'Select a value')
      }
      buttonSize={buttonSize}
      menu={
        <SearchableMenu
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.MEDIUM}
          optionDatas={menuOptions}
          selectedValue={selectedTag}
          onSelect={handleSelect}
        />
      }
      disabled={disabled}
    />
  );
}

interface TaggedUnionEditorProps {
  config: JSONObject | null;
  configShape: GetPipelineConfigSpecType<'taggedUnion'>;
  onChangeConfig: (config: JSONValue) => void;
  uiSchema?: TaggedUnionUISchema;
  disabled?: boolean;
  forceSingleColumnDisplay?: boolean;
  buttonSize?: ButtonSize;
}

export default function TaggedUnionEditor({
  config,
  configShape,
  onChangeConfig,
  uiSchema,
  disabled,
  forceSingleColumnDisplay,
  buttonSize = ButtonSize.MEDIUM,
}: TaggedUnionEditorProps): JSX.Element {
  const {inputDataFrames} = useStepConfigEditorContext();
  function handleChangeConfig(newConfig: JSONValue): void {
    if (uiSchema?.[ON_CHANGE_CONFIG_MIDDLEWARE_KEY]) {
      onChangeConfig(uiSchema[ON_CHANGE_CONFIG_MIDDLEWARE_KEY](newConfig, config, inputDataFrames));
    } else {
      onChangeConfig(newConfig);
    }
  }

  const tagKey = configShape.tagKey;
  const selectedTag = config?.[tagKey] ?? null;
  assert(selectedTag == null || typeof selectedTag === 'string', 'Invalid tag key found');

  const selectedChoice =
    selectedTag != null
      ? (configShape.choices.find((choice) => {
          const tagFieldSpec = choice.fields[tagKey];
          assert(isTaggedUnionChoice(tagFieldSpec), `Tag key ${tagKey} not found in choice ${choice}`);
          return tagFieldSpec.value === selectedTag;
        }) ?? null)
      : null;

  const tagField = {
    variableName: uiSchema?.choiceSettings?.[tagKey]?.displayName ?? tagKey,
    infoText: uiSchema?.infoText,
    editor: (
      <TagChoiceDropdown
        configShape={configShape}
        onChangeChoice={(newChoiceShape) => {
          handleChangeConfig(
            jsonSpecToInitialValue(
              newChoiceShape,
              getFieldSettingsIfPresent(uiSchema?.choiceSettings, tagKey)
            )
          );
        }}
        selectedTag={selectedTag}
        disabled={disabled}
        uiSchema={uiSchema}
        buttonSize={buttonSize}
      />
    ),
  };

  return (
    <StepConfigFieldsTemplate
      numColumns={forceSingleColumnDisplay ? 1 : 2}
      fields={
        selectedChoice == null
          ? [tagField]
          : [
              tagField,
              ...getSortedSubfields(selectedChoice.fields, uiSchema)
                // Remove the tag key from the list of fields so it's not rendered twice
                .filter(([variableName]) => variableName !== tagKey)
                .map(([variableName, variableShape]) => ({
                  variableName: uiSchema?.choiceSettings?.[variableName]?.displayName ?? variableName,
                  infoText: uiSchema?.infoText,
                  addIcon:
                    variableShape.kind === 'array' ? (
                      <AddArrayElementIcon
                        specShape={variableShape}
                        config={(config?.[variableName] ?? []) as JSONArray | null}
                        onChangeConfig={(newValue) => {
                          handleChangeConfig({...config, [variableName]: newValue});
                        }}
                        elementUiSchema={getFieldSettingsIfPresent(uiSchema?.choiceSettings, variableName)}
                        dataTestAttrsKey={variableName}
                      />
                    ) : undefined,
                  editor: (
                    <StepConfigEditorRouter
                      config={config?.[variableName] ?? null}
                      configShape={variableShape}
                      dataTestAttrs={{key: variableName}}
                      onChangeConfig={(newConfig) => {
                        handleChangeConfig({...config, [variableName]: newConfig});
                      }}
                      uiSchema={getFieldSettingsIfPresent(uiSchema?.choiceSettings, variableName)}
                      disabled={disabled}
                    />
                  ),
                })),
            ]
      }
    />
  );
}

const styles = StyleSheet.create({
  grow: {
    flexGrow: 1,
  },
});
